import {
    FC,
    ReactNode,
    MouseEventHandler,
    ComponentProps,
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import {
    ScrollCarouselProvider,
    useScrollCarousel,
} from 'dibs-scroll-carousel/exports/ScrollCarouselContext';
import { InnerScrollCarousel } from 'dibs-scroll-carousel/exports/InnerScrollCarousel';
import { Link } from 'dibs-elements/exports/Link';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import { useShowVimeoVideo } from '../../../utils/useShowVimeoVideo';
import { CarouselThumb } from './CarouselThumb';
import { Image } from './CarouselItems/Image';
import { VimeoVideo } from './CarouselItems/VimeoVideo';
import { ViewInRoom } from './CarouselItems/ViewInRoom';
import { RequestMoreImages } from './CarouselItems/RequestMoreImages';
import { Counter } from './Counter';
import { PdpRespZoomModalLoader } from '../../PdpRespZoomModal/PdpRespZoomModalLoader';
import videoTracking from '../../shared/PdpSharedVimeoVideo/tracking';
import { trackMainCarouselClick, trackViewRoomDisplayed, trackArrowClick } from './tracking';

import {
    PdpRespSharedHeroCarousel_item$key,
    PdpRespSharedHeroCarousel_item$data,
} from './__generated__/PdpRespSharedHeroCarousel_item.graphql';
import { PdpRespSharedHeroCarousel_viewer$key } from './__generated__/PdpRespSharedHeroCarousel_viewer.graphql';
import { PdpRespSharedHeroCarousel_user$key } from './__generated__/PdpRespSharedHeroCarousel_user.graphql';

import styles from './PdpRespSharedHeroCarousel.scss';

const THUMB_ITEMS_TO_SHOW = 6;
const VIDEO_INDEX = 1;

type CarouselItemPhoto = NonNullable<
    ArrayElement<PdpRespSharedHeroCarousel_item$data['sortedPhotos']>
> & { __type: 'photo' };

type CarouselItemVideo = NonNullable<PdpRespSharedHeroCarousel_item$data['videoVimeo']> & {
    __type: 'video';
};
type CarouselItemViewInRoom = NonNullable<
    ArrayElement<PdpRespSharedHeroCarousel_item$data['viewInRoomPhotos']>
> & { __type: 'viewInRoom' };
type CarouselItemRequestMore = { __type: 'requestMore' };

type CarouselItem =
    | CarouselItemPhoto
    | CarouselItemVideo
    | CarouselItemViewInRoom
    | CarouselItemRequestMore;

type SlideChangeType = 'next' | 'previous' | 'other';

const isPhoto = (item: CarouselItem): item is CarouselItemPhoto =>
    '__typename' in item && item.__typename === 'ItemPhoto';

const ZoomModal: FC<{
    isOpen: boolean;
    onCloseClick: () => void;
    carouselItems: CarouselItem[];
    title: string | null;
    image: string | null;
    itemId: string | null;
    userId: string | null;
    currency: string;
}> = ({ isOpen, onCloseClick, carouselItems, title, image, itemId, userId, currency }) => {
    const { activeSlide, setCurrentIndex } = useScrollCarousel();

    const onModalCloseClick = useCallback(
        ({ index }: $TSFixMe) => {
            onCloseClick();
            /**
             * Hack to make old carousels to be compatible with scroll carousel. Additionally
             * need to use setTimeout to make sure that all scroll preventions that are added
             * by the modal are removed before setting the index.
             */
            setTimeout(() => {
                setCurrentIndex(
                    carouselItems.length > THUMB_ITEMS_TO_SHOW
                        ? {
                              index: index + THUMB_ITEMS_TO_SHOW,
                              backfillStartCount: THUMB_ITEMS_TO_SHOW,
                          }
                        : {
                              index,
                              backfillStartCount: 0,
                          }
                );
            }, 50);
        },
        [setCurrentIndex, onCloseClick, carouselItems]
    );

    const zoomMedia = useMemo(() => {
        return carouselItems
            .map(carouselItem => {
                if (carouselItem.__type === 'photo') {
                    return {
                        src: carouselItem.masterOrZoomPath,
                        alt: carouselItem.alt,
                    };
                } else if (carouselItem.__type === 'video') {
                    return {
                        type: 'VIMEO',
                        src: carouselItem.fileByVideoSize?.link,
                        thumbnail: carouselItem.thumbnail,
                        vimeoPlayerOptions: {
                            quality: '4k',
                            id: carouselItem.vimeoId,
                            tracking: videoTracking,
                        },
                    };
                }

                return null;
            })
            .filter(filterNulls);
    }, [carouselItems]);

    return title && image && itemId ? (
        <PdpRespZoomModalLoader
            //@ts-ignore
            relayVariables={{ itemId, fetchUser: !!userId, userId }}
            isOpen={isOpen}
            totalItems={carouselItems.length}
            onCloseClick={onModalCloseClick}
            startingIndex={activeSlide - 1}
            zoomMedia={zoomMedia}
            display
            itemTitle={title}
            itemImage={image}
            itemId={itemId}
            currency={currency}
        />
    ) : null;
};

const CarouselItemWrapper: FC<{
    type: CarouselItem['__type'];
    size: 'small' | 'medium' | 'large';
    openZoomModal: () => void;
    dataTn: string;
    children: ReactNode;
}> = ({ type, size, openZoomModal, dataTn, children }) => {
    const { activeSlide, totalSlides } = useScrollCarousel();
    const isZoomable = type === 'photo' || type === 'viewInRoom';

    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            if (isZoomable) {
                trackMainCarouselClick(
                    {
                        activeSlide,
                        totalSlides,
                    },
                    event
                );
            }
            openZoomModal();
        },
        [openZoomModal, activeSlide, totalSlides, isZoomable]
    );

    return isZoomable ? (
        <Link
            className={classnames(styles.item, styles[size], 'is-zoom')}
            onClick={onClick}
            dataTn={dataTn}
        >
            {children}
        </Link>
    ) : (
        <div className={classnames(styles.item, styles[size])} data-tn={dataTn}>
            {children}
        </div>
    );
};

const viewerFragment = graphql`
    fragment PdpRespSharedHeroCarousel_viewer on Viewer {
        ...useShowVimeoVideo_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSharedHeroCarousel_item on Item {
        ...useShowVimeoVideo_item
        ...Image_item
        ...ViewInRoom_item
        ...RequestMoreImages_item
        isMultiSku
        sortedPhotos {
            ...CarouselThumb_sortedPhoto
            ...Image_sortedPhoto
            __typename
            masterOrZoomPath
            largePath
            alt
            imageSkuXrefs {
                skuId
            }
        }
        videoVimeo {
            ...CarouselThumb_videoVimeo
            ...VimeoVideo_videoVimeo
            thumbnail(size: w100h75, includePlayButton: false, pad: false)
            vimeoId
            fileByVideoSize(size: small) {
                link
            }
        }
        viewInRoomPhotos {
            ...CarouselThumb_viewInRoomPhoto
            ...ViewInRoom_viewInRoomPhoto
        }
        hasPrivateListing
        seller {
            isAccessible
        }
        serviceId
        title
        firstPhotoWebPath(size: small)
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedHeroCarousel_user on User {
        ...ViewInRoom_user
        ...RequestMoreImages_user
        serviceId
    }
`;

const PdpRespSharedHeroCarouselComponent: FC<{
    item: PdpRespSharedHeroCarousel_item$key;
    user: PdpRespSharedHeroCarousel_user$key | null;
    carouselItems: CarouselItem[];
    currency: string;
    disableModals?: boolean;
    size?: 'small' | 'medium' | 'large';
    afterDisplay: boolean;
    selectedSkuId?: string;
    children?: ReactNode;
}> = ({
    item: itemRef,
    user: userRef,
    carouselItems,
    currency,
    disableModals,
    size = 'large',
    afterDisplay,
    selectedSkuId,
    children,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const userId = user?.serviceId || null;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openZoomModal = useCallback(() => {
        if (disableModals) {
            return;
        }
        setIsModalOpen(true);
    }, [disableModals]);
    const closeZoomModal = useCallback(() => setIsModalOpen(false), []);

    const { activeSlide, totalSlides, resetCurrentIndex } = useScrollCarousel();
    const slideChangeTypeRef = useRef<SlideChangeType>('other');

    const onArrowClick: NonNullable<ComponentProps<typeof InnerScrollCarousel>['onArrowClick']> =
        useCallback(({ direction }) => {
            slideChangeTypeRef.current = direction;
        }, []);

    useEffect(() => {
        const currentItem = carouselItems[activeSlide - 1];
        if (slideChangeTypeRef.current === 'next' || slideChangeTypeRef.current === 'previous') {
            trackArrowClick({
                slideType: currentItem.__type,
                activeSlide,
                totalSlides,
                arrowDirection: slideChangeTypeRef.current,
            });
        }

        if (currentItem.__type === 'viewInRoom') {
            trackViewRoomDisplayed({ activeSlide, totalSlides });
        }

        slideChangeTypeRef.current = 'other';
    }, [activeSlide, totalSlides, carouselItems]);

    const selectedSkuIdRef = useRef<string | undefined>(selectedSkuId);
    useEffect(() => {
        if (selectedSkuIdRef.current !== selectedSkuId) {
            selectedSkuIdRef.current = selectedSkuId;
            resetCurrentIndex();
        }
    }, [resetCurrentIndex, selectedSkuId]);

    return (
        <>
            <ZoomModal
                isOpen={isModalOpen}
                onCloseClick={closeZoomModal}
                carouselItems={carouselItems}
                title={item.title}
                image={item.firstPhotoWebPath}
                itemId={item.serviceId}
                userId={userId}
                currency={currency}
            />

            <div className={`${styles.wrapper} enable-click-on-preview`}>
                <div className={`${styles.thumbsWrapper} ${styles[size]}`}>
                    {afterDisplay && (
                        <InnerScrollCarousel
                            orientation="vertical"
                            itemsToShow={THUMB_ITEMS_TO_SHOW}
                        >
                            {carouselItems.map((carouselItem, index) => {
                                if (carouselItem.__type === 'photo') {
                                    return (
                                        <CarouselThumb
                                            key={index}
                                            type={carouselItem.__type}
                                            size={size}
                                            sortedPhoto={carouselItem}
                                            videoVimeo={null}
                                            viewInRoomPhoto={null}
                                        />
                                    );
                                } else if (carouselItem.__type === 'video') {
                                    return (
                                        <CarouselThumb
                                            key={index}
                                            type={carouselItem.__type}
                                            size={size}
                                            sortedPhoto={null}
                                            videoVimeo={carouselItem}
                                            viewInRoomPhoto={null}
                                        />
                                    );
                                } else if (carouselItem.__type === 'viewInRoom') {
                                    return (
                                        <CarouselThumb
                                            key={index}
                                            type={carouselItem.__type}
                                            size={size}
                                            sortedPhoto={null}
                                            videoVimeo={null}
                                            viewInRoomPhoto={carouselItem}
                                        />
                                    );
                                } else if (carouselItem.__type === 'requestMore') {
                                    return (
                                        <CarouselThumb
                                            key={index}
                                            type={carouselItem.__type}
                                            size={size}
                                            sortedPhoto={null}
                                            videoVimeo={null}
                                            viewInRoomPhoto={null}
                                        />
                                    );
                                }

                                //render empty div for unknown types to avoid breaking the carousel
                                return <div />;
                            })}
                        </InnerScrollCarousel>
                    )}
                </div>

                <div className={`${styles.displayWrapper} ${styles[size]}`}>
                    <InnerScrollCarousel
                        showArrows
                        orientation="horizontal"
                        itemsToShow={1}
                        animation="none"
                        onArrowClick={onArrowClick}
                        dataTn="pdp-image-carousel"
                    >
                        {carouselItems.map((carouselItem, index) => {
                            let child = null;
                            if (carouselItem.__type === 'photo') {
                                child = (
                                    <Image
                                        eagerLoad={index === 0}
                                        item={item}
                                        sortedPhoto={carouselItem}
                                        size={size}
                                    />
                                );
                            } else if (carouselItem.__type === 'video') {
                                child = <VimeoVideo videoVimeo={carouselItem} />;
                            } else if (
                                carouselItem.__type === 'viewInRoom' &&
                                //type guard to ensure that carouselItems[0] is a CarouselItemPhoto type
                                isPhoto(carouselItems[0])
                            ) {
                                child = (
                                    <ViewInRoom
                                        user={user}
                                        item={item}
                                        viewInRoomPhoto={carouselItem}
                                        mainImagePath={{
                                            imagePath: carouselItems[0].largePath,
                                            masterOrZoomPath: carouselItems[0].masterOrZoomPath,
                                        }}
                                    />
                                );
                            } else if (carouselItem.__type === 'requestMore') {
                                child = (
                                    <RequestMoreImages
                                        user={user}
                                        item={item}
                                        currency={currency}
                                        disableRequestMoreImagesModal={disableModals}
                                    />
                                );
                            }

                            return (
                                <CarouselItemWrapper
                                    key={index}
                                    type={carouselItem.__type}
                                    size={size}
                                    openZoomModal={openZoomModal}
                                    dataTn={`pdp-image-carousel-image-${index + 1}`}
                                >
                                    {child}
                                </CarouselItemWrapper>
                            );
                        })}
                    </InnerScrollCarousel>
                    <Counter />

                    {children}
                </div>
            </div>
        </>
    );
};

export const PdpRespSharedHeroCarousel: FC<
    Omit<
        ComponentProps<typeof PdpRespSharedHeroCarouselComponent> & {
            viewer: PdpRespSharedHeroCarousel_viewer$key;
        },
        'carouselItems'
    >
> = props => {
    const { viewer: viewerRef, item: itemRef, selectedSkuId } = props;
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const { sortedPhotos, videoVimeo, viewInRoomPhotos, hasPrivateListing, seller, isMultiSku } =
        item;

    const showVideo = useShowVimeoVideo({ viewer, item });
    const showRequestMore = !hasPrivateListing && seller?.isAccessible;

    const photos = sortedPhotos || [];
    const roomPhotos = viewInRoomPhotos || [];
    const prevPhotosRef = useRef<CarouselItemPhoto[]>(
        photos.filter(filterNulls).map<CarouselItemPhoto>(photo => ({
            __type: 'photo',
            ...photo,
        }))
    );

    const carouselItems: CarouselItem[] = useMemo(() => {
        let _carouselItems: CarouselItem[] = [...prevPhotosRef.current];

        const selectedSkuPhotos: CarouselItemPhoto[] = [];
        if (isMultiSku) {
            const productPhotos: CarouselItemPhoto[] = [];
            const remainingPhotos: CarouselItemPhoto[] = [];
            prevPhotosRef.current.forEach(photo => {
                if (photo.imageSkuXrefs?.some(xref => xref?.skuId === selectedSkuId)) {
                    selectedSkuPhotos.push(photo);
                } else if (photo.imageSkuXrefs?.length === 0) {
                    productPhotos.push(photo);
                } else {
                    remainingPhotos.push(photo);
                }
            });

            if (selectedSkuPhotos.length !== 0) {
                prevPhotosRef.current = [
                    ...selectedSkuPhotos,
                    ...productPhotos,
                    ...remainingPhotos,
                ];
                _carouselItems = [...prevPhotosRef.current];
            }
        }

        if (showVideo && videoVimeo) {
            const videoIndex = selectedSkuPhotos.length + VIDEO_INDEX;
            _carouselItems.splice(videoIndex, 0, {
                __type: 'video',
                ...videoVimeo,
            });
        }

        if (roomPhotos.length > 0) {
            _carouselItems.push(
                ...roomPhotos.filter(filterNulls).map<CarouselItem>(viewInRoomPhoto => ({
                    __type: 'viewInRoom',
                    ...viewInRoomPhoto,
                }))
            );
        }

        if (showRequestMore) {
            _carouselItems.push({
                __type: 'requestMore',
            });
        }

        return _carouselItems;
    }, [showVideo, videoVimeo, roomPhotos, showRequestMore, selectedSkuId, isMultiSku]);

    return (
        <ScrollCarouselProvider
            isInfinite
            totalItems={carouselItems.length}
            largestItemsToShow={THUMB_ITEMS_TO_SHOW}
            stepSize={1}
        >
            <PdpRespSharedHeroCarouselComponent {...props} carouselItems={carouselItems} />
        </ScrollCarouselProvider>
    );
};
